<template>
  <Teleport to="body">
    <!-- Backdrop: click-to-dismiss; fades in. -->
    <transition name="v2-share-backdrop">
      <div
        v-if="isOpen"
        class="v2-share__backdrop"
        aria-hidden="true"
        @click="close"
      />
    </transition>

    <!-- Panel: spring-in scale+translate. -->
    <transition name="v2-share-panel">
      <div
        v-if="isOpen"
        class="v2-share"
        role="dialog"
        aria-modal="true"
        aria-labelledby="v2-share-title"
        @keydown.esc="close"
        @keydown.tab="trapFocus"
      >
        <div ref="panel" class="v2-share__panel" @click.stop>
          <!-- Pink accent bar (brand signature) -->
          <span class="v2-share__accent" aria-hidden="true" />

          <!-- Close X -->
          <button
            ref="closeBtn"
            type="button"
            class="v2-share__close"
            aria-label="Close"
            @click="close"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <div class="v2-share__content">
          <!-- Header -->
          <header class="v2-share__header">
            <span class="v2-share__eyebrow">
              <span class="v2-share__eyebrow-dot" aria-hidden="true" />
              Share this campaign
            </span>
            <h3 id="v2-share-title" class="v2-share__title">{{ campaignName }}</h3>
            <p class="v2-share__desc">Every share brings a donor closer. Spread the word.</p>
          </header>

          <!-- Copy link row -->
          <div class="v2-share__copy">
            <input
              ref="linkInput"
              type="text"
              class="v2-share__copy-input"
              :value="shareUrl"
              readonly
              aria-label="Campaign link"
              @focus="$event.target.select()"
            >
            <button
              type="button"
              class="v2-share__copy-btn"
              :class="{ 'is-copied': justCopied }"
              @click="copyLink"
            >
              <svg v-if="!justCopied" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span>{{ justCopied ? 'Copied!' : 'Copy link' }}</span>
            </button>
          </div>

          <!-- Social share buttons -->
          <div class="v2-share__grid">
            <button
              v-for="opt in shareOptions"
              :key="opt.key"
              type="button"
              :class="['v2-share__btn', 'v2-share__btn--' + opt.key]"
              @click="openShare(opt)"
            >
              <span class="v2-share__btn-icon" aria-hidden="true" v-html="opt.icon" />
              <span class="v2-share__btn-label">{{ opt.label }}</span>
            </button>
          </div>

          <!-- QR code: scan-to-share for in-person moments (volleyball
               sidelines, post-game announcements, fundraising tables).
               Generated client-side via the qrcode package so the URL
               doesn't leak to a third-party. -->
          <div class="v2-share__qr" v-if="qrSvg">
            <div class="v2-share__qr-label">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <path d="M14 14h7v7h-7z"/>
              </svg>
              <span>Or scan to share in person</span>
            </div>
            <div class="v2-share__qr-image" v-html="qrSvg" aria-label="QR code for this campaign" role="img" />
          </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { acquirePageScrollLock } from '../../../../composables/usePageScrollLock'
// Share-option construction, device gating, and robust link-opening all live
// in the canonical useCampaignShare module so this modal and the support
// onboarding flow never drift apart (same icons, same order, same behavior).
import { buildShareOptions, cleanCampaignShareUrl, openShareOption, isDesktop } from '../../../../composables/useCampaignShare'

export default {
  name: 'V2ShareModal',
  data() {
    return {
      isOpen: false,
      justCopied: false,
      // Cached at open-time so the modal doesn't re-render the URL
      // from a rapidly-changing route.
      shareUrl: '',
      campaignName: 'this campaign',
      previouslyFocused: null,
      _copyTimeout: null,
      // QR SVG markup (generated lazily on open). Inlined via v-html
      // in the template. Empty until the qrcode module loads.
      qrSvg: '',
    }
  },
  computed: {
    // Canonical, ordered share set from the shared module:
    // facebook, twitter, linkedin, whatsapp, email, [sms only on mobile],
    // instagram, copy. SMS is dropped on desktop (no Messages app to hand
    // off to); copy is a copy-action; instagram copies the link AND opens
    // Instagram (no web share-intent — Rick 2026-06-11).
    shareOptions() {
      return buildShareOptions({
        url: this.shareUrl,
        campaignName: this.campaignName,
        desktop: isDesktop(),
      })
    },
  },
  created() {
    this._disposed = false
    this.emitter.on('open-share-dialog', this.openFromEvent)
  },
  beforeUnmount() {
    this._disposed = true
    this.emitter.off('open-share-dialog', this.openFromEvent)
    clearTimeout(this._copyTimeout)
    document.removeEventListener('keydown', this._onEsc)
    this._releasePageScroll?.()
    this._releasePageScroll = null
  },
  methods: {
    openFromEvent(payload = {}) {
      if (this._disposed) return
      this._returnFocus = payload.returnFocus || document.activeElement
      this.shareUrl = payload.url || cleanCampaignShareUrl()
      this.campaignName = payload.campaignName || 'this campaign'

      // On mobile with Web Share API, use the native OS share sheet
      // instead of our custom modal — dramatically better UX than any
      // web UI can match (direct targets to Messages, WhatsApp, Signal,
      // AirDrop, etc.). Desktop keeps the custom modal because desktop
      // Web Share implementations are inconsistent and the modal's
      // side-by-side social buttons + QR + copy-link are more useful
      // at desk-width than an OS share sheet.
      if (this._shouldUseNativeShare() && typeof navigator.share === 'function') {
        navigator.share({
          title: this.campaignName,
          text: `Supporting ${this.campaignName} — Side-Out Foundation Dig Pink`,
          url: this.shareUrl,
        }).catch(err => {
          // User dismissed the sheet: nothing to do. Any other failure:
          // fall back to the custom modal.
          if (err && err.name !== 'AbortError') this.open()
        })
        return
      }
      this.open()
    },
    _shouldUseNativeShare() {
      // Coarse but effective: treat <768px viewports as mobile. The
      // Web Share API is most reliable on iOS Safari + Android Chrome
      // where the OS share sheet is integrated.
      return typeof window !== 'undefined' && window.innerWidth < 768
    },
    async _generateQr() {
      // Dynamic import keeps qrcode out of the initial bundle — only
      // loaded when the user actually opens the share modal.
      try {
        const QRCode = (await import('qrcode')).default
        this.qrSvg = await QRCode.toString(this.shareUrl, {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 160,
          color: { dark: '#1D2549', light: '#ffffff' }, // sideout-navy on white
        })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('V2ShareModal: QR generation failed', err)
        this.qrSvg = ''
      }
    },
    open() {
      // A native-share promise can reject after this component is removed.
      if (this._disposed || this.isOpen) return
      this.previouslyFocused = this._returnFocus || document.activeElement
      this._releasePageScroll = acquirePageScrollLock()
      this.justCopied = false
      this.qrSvg = ''
      this.isOpen = true
      this._generateQr()
      this.$nextTick(() => {
        if (this.$refs.closeBtn && this.$refs.closeBtn.focus) {
          this.$refs.closeBtn.focus({ preventScroll: true })
        }
      })
      document.addEventListener('keydown', this._onEsc)
    },
    close() {
      if (!this.isOpen) return
      this.isOpen = false
      this.justCopied = false
      clearTimeout(this._copyTimeout)
      this._releasePageScroll?.()
      this._releasePageScroll = null
      document.removeEventListener('keydown', this._onEsc)
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.$nextTick(() => this.previouslyFocused.focus({ preventScroll: true }))
      }
    },
    trapFocus(event) {
      const controls = [...this.$refs.panel.querySelectorAll('button, input, a[href], [tabindex="0"]')]
        .filter(el => !el.disabled && el.getClientRects().length)
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    },
    async copyLink() {
      const url = this.shareUrl
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url)
        } else {
          // Older browsers / insecure contexts fallback.
          const input = this.$refs.linkInput
          input.select()
          input.setSelectionRange(0, url.length)
          document.execCommand('copy')
        }
        this.justCopied = true
        clearTimeout(this._copyTimeout)
        this._copyTimeout = setTimeout(() => { this.justCopied = false }, 2000)
      } catch (e) {
        // If clipboard fails, select the input so the user can Cmd-C.
        if (this.$refs.linkInput) this.$refs.linkInput.select()
      }
    },
    openShare(opt) {
      // Delegate to the shared opener: copy-actions (copy) reuse our copyLink
      // (which also flips the "Copied!" state + reselects the input on
      // failure); instagram copies the link then opens Instagram in a new
      // tab; popup intents open centered and fall back to copyLink if the
      // browser blocks the popup; mailto:/sms: navigate.
      openShareOption(opt, { onCopy: () => this.copyLink() })
    },
    _onEsc(e) {
      if (e.key === 'Escape') this.close()
    },
  },
}
</script>

<style scoped>
/* ─── Backdrop ────────────────────────────────────────────── */
.v2-share__backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  background:
    radial-gradient(ellipse at center,
      rgba(29, 37, 73, 0.58) 0%,
      rgba(10, 13, 28, 0.78) 65%,
      rgba(10, 13, 28, 0.85) 100%);
  -webkit-backdrop-filter: blur(8px) saturate(140%);
  backdrop-filter: blur(8px) saturate(140%);
}

/* ─── Dialog container ────────────────────────────────────── */
.v2-share {
  position: fixed;
  inset: 0;
  z-index: 51;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(16px, 4vw, 48px);
  pointer-events: none;
  height: 100vh;
  height: 100dvh;
}
.v2-share__panel {
  pointer-events: auto;
  position: relative;
  width: 100%;
  max-width: 30rem;
  background: linear-gradient(180deg, #ffffff 0%, #fefafc 100%);
  border-radius: 20px;
  display: flex;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  font-family: 'Red Hat Display', sans-serif;
}
.v2-share__content {
  width: 100%;
  padding: 58px 28px 24px;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}

.v2-share__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg,
    transparent 0%,
    var(--sideout-pink) 20%,
    #ff4fa5 50%,
    var(--sideout-pink) 80%,
    transparent 100%);
  pointer-events: none;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
}

/* ─── Close X ─────────────────────────────────────────────── */
.v2-share__close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(29, 37, 73, 0.08);
  border-radius: 9999px;
  color: var(--sideout-navy);
  cursor: pointer;
  transition:
    background 220ms cubic-bezier(0.4, 0, 0.2, 1),
    border-color 220ms cubic-bezier(0.4, 0, 0.2, 1),
    color 220ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1);
}
.v2-share__close svg { transition: transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1); }
.v2-share__close:hover {
  background: var(--sideout-pink);
  border-color: var(--sideout-pink);
  color: #ffffff;
  transform: scale(1.06);
}
.v2-share__close:hover svg { transform: rotate(90deg); }
.v2-share__close:focus-visible {
  outline: 2px solid var(--sideout-pink);
  outline-offset: 3px;
}

/* ─── Header ──────────────────────────────────────────────── */
.v2-share__header {
  text-align: center;
  padding: 0 8px 18px;
}
.v2-share__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--sideout-pink);
}
.v2-share__eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: var(--sideout-pink);
  box-shadow: 0 0 0 4px rgba(218, 24, 132, 0.12);
}
.v2-share__title {
  margin: 10px 0 4px;
  font-size: clamp(20px, 2.6vw, 24px);
  font-weight: 900;
  line-height: 1.15;
  color: var(--sideout-navy);
  letter-spacing: -0.015em;
  text-wrap: balance;
  overflow-wrap: anywhere;
}
.v2-share__desc {
  margin: 0;
  font-size: 15px; /* was 13px — share-dialog description, mobile legibility (client 2026-05-29) */
  line-height: 1.5;
  color: var(--sideout-muted);
}

/* ─── Copy link row ──────────────────────────────────────── */
.v2-share__copy {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 20px;
}
.v2-share__copy-input {
  width: 100%;
  padding: 10px 14px;
  font-family: 'Red Hat Text', 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px;
  color: var(--sideout-muted);
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  outline: none;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  transition: border-color 180ms, box-shadow 180ms;
}
.v2-share__copy-input:focus {
  border-color: #F0A3CE;
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.12);
  color: var(--sideout-navy);
  background: #ffffff;
}
.v2-share__copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ffffff;
  background: linear-gradient(135deg, #DA1884 0%, #BC0E65 100%);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    background 300ms;
  white-space: nowrap;
}
.v2-share__copy-btn:hover:not(.is-copied) {
  transform: translateY(-1px);
}
.v2-share__copy-btn.is-copied {
  background: linear-gradient(135deg, #16A34A 0%, #0F8036 100%);
  cursor: default;
}

/* ─── Social grid ────────────────────────────────────────── */
.v2-share__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.v2-share__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1.5px solid #E5E7EB;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--sideout-navy);
  transition:
    border-color 180ms cubic-bezier(0.22, 1, 0.36, 1),
    background 180ms cubic-bezier(0.22, 1, 0.36, 1),
    color 180ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
  /* Staggered entrance — each button rises into place 60ms after the
     previous one, producing a cascade that signals "these are the paths
     forward." The animation only fires on first mount; subsequent opens
     use v-if which re-mounts the buttons fresh. */
  opacity: 0;
  animation: v2-share-btn-in 420ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.v2-share__btn:nth-child(1) { animation-delay: 80ms; }
.v2-share__btn:nth-child(2) { animation-delay: 140ms; }
.v2-share__btn:nth-child(3) { animation-delay: 200ms; }
.v2-share__btn:nth-child(4) { animation-delay: 260ms; }
.v2-share__btn:nth-child(5) { animation-delay: 320ms; }
.v2-share__btn:nth-child(6) { animation-delay: 380ms; }
.v2-share__btn:nth-child(7) { animation-delay: 440ms; }
.v2-share__btn:nth-child(8) { animation-delay: 500ms; }
@keyframes v2-share-btn-in {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .v2-share__btn { animation: none; opacity: 1; }
}
.v2-share__btn:hover {
  transform: translateY(-1px);
}
.v2-share__btn:active { transform: translateY(0) scale(0.98); }

/* QR code block — appears below the social grid when qrSvg populates
   (async after modal open). Navy-on-white for clean contrast; centered
   under a small label. */
.v2-share__qr {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #EFE1EC;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  opacity: 0;
  animation: v2-share-qr-in 520ms cubic-bezier(0.16, 1, 0.3, 1) 340ms forwards;
}
.v2-share__qr-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--sideout-muted);
}
.v2-share__qr-image {
  /* 160×160 container; the SVG inside fits naturally. Slight rounded
     corners + padding lift the QR off the white panel just a bit. */
  width: 160px;
  height: 160px;
  padding: 8px;
  background: #ffffff;
  border: 1px solid #EFE1EC;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.v2-share__qr-image :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}
@keyframes v2-share-qr-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .v2-share__qr { animation: none; opacity: 1; }
}

/* Brand-color hovers per platform. Solid fills only (no gradients) per the
   V2 button convention. Instagram has no single brand hex, so it borrows the
   brand pink; sms + copy match email's pink for a coherent "owned" set. */
.v2-share__btn--facebook:hover  { background: #1877F2; border-color: #1877F2; color: #ffffff; }
.v2-share__btn--twitter:hover   { background: #000000; border-color: #000000; color: #ffffff; }
.v2-share__btn--linkedin:hover  { background: #0A66C2; border-color: #0A66C2; color: #ffffff; }
.v2-share__btn--whatsapp:hover  { background: #25D366; border-color: #25D366; color: #ffffff; }
.v2-share__btn--email:hover     { background: var(--sideout-pink); border-color: var(--sideout-pink); color: #ffffff; }
.v2-share__btn--sms:hover       { background: var(--sideout-pink); border-color: var(--sideout-pink); color: #ffffff; }
.v2-share__btn--instagram:hover { background: #E1306C; border-color: #E1306C; color: #ffffff; }
.v2-share__btn--copy:hover      { background: var(--sideout-pink); border-color: var(--sideout-pink); color: #ffffff; }

.v2-share__btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
}
.v2-share__btn-label {
  line-height: 1;
  letter-spacing: 0.01em;
}

/* ─── Mobile refinement ──────────────────────────────────── */
@media (max-width: 480px) {
  .v2-share__panel { border-radius: 16px; }
  .v2-share__content { padding: 58px 20px 20px; }
  .v2-share__header { padding: 0 4px 14px; }
  .v2-share__title { font-size: 20px; }
  .v2-share__copy { grid-template-columns: 1fr; gap: 8px; }
  .v2-share__copy-btn { width: 100%; }
}
/* iOS auto-zooms inputs with computed font-size < 16px, breaking
   the layout once the viewport shifts. Force 16px at mobile so
   focusing the copy-link input never triggers the zoom. */
@media (max-width: 640px) {
  .v2-share__copy-input { font-size: 16px; }
}

/* ─── Transitions ────────────────────────────────────────── */
.v2-share-backdrop-enter-active,
.v2-share-backdrop-leave-active {
  transition: opacity 300ms cubic-bezier(0.4, 0, 0.2, 1);
}
.v2-share-backdrop-enter-from,
.v2-share-backdrop-leave-to { opacity: 0; }

.v2-share-panel-enter-active { transition: opacity 480ms; }
.v2-share-panel-enter-active .v2-share__panel {
  animation: v2-share-panel-in 520ms cubic-bezier(0.22, 1, 0.36, 1) 60ms both;
}
.v2-share-panel-leave-active { transition: opacity 220ms; }
.v2-share-panel-leave-active .v2-share__panel {
  animation: v2-share-panel-out 260ms cubic-bezier(0.4, 0, 0.6, 1) both;
}
.v2-share-panel-enter-from, .v2-share-panel-leave-to { opacity: 0; }

@keyframes v2-share-panel-in {
  0%   { opacity: 0; transform: translate3d(0, 20px, 0) scale(0.96); }
  60%  { opacity: 1; transform: translate3d(0, -2px, 0) scale(1.01); }
  100% { opacity: 1; transform: translate3d(0, 0, 0)    scale(1); }
}
@keyframes v2-share-panel-out {
  from { opacity: 1; transform: translate3d(0, 0, 0)    scale(1); }
  to   { opacity: 0; transform: translate3d(0, 14px, 0) scale(0.96); }
}

@media (prefers-reduced-motion: reduce) {
  .v2-share-panel-enter-active .v2-share__panel,
  .v2-share-panel-leave-active .v2-share__panel {
    animation-duration: 140ms !important;
    animation-delay: 0 !important;
  }
  .v2-share-backdrop-enter-active,
  .v2-share-backdrop-leave-active { transition-duration: 100ms; }
  .v2-share__btn, .v2-share__copy-btn { transition-duration: 80ms; }
}
</style>
